/**
 * FeatureGate — mostra un overlay "Piano richiesto" quando una feature è bloccata.
 * Uso: <FeatureGate feature="smart_home" requiredPlan="Professional">...</FeatureGate>
 */
import React from 'react';
import { Lock, ArrowRight } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';

const PLAN_ORDER = { essenziale: 0, professional: 1, elite: 2, lifetime: 3 };

const FEATURE_PLAN_MAP = {
  smart_home:      { requiredPlan: 'professional', label: 'Smart Home & Dispositivi' },
  supplier_portal: { requiredPlan: 'professional', label: 'Portale Fornitori' },
  revenue_export:  { requiredPlan: 'professional', label: 'Export CSV Ricavi' },
  rate_heatmap:    { requiredPlan: 'professional', label: 'Rate Calendar Heatmap' },
  access_log:      { requiredPlan: 'professional', label: 'Log Accessi Dettagliato' },
  i18n_editor:     { requiredPlan: 'elite',        label: 'Editor Testi i18n' },
  white_label:     { requiredPlan: 'professional', label: 'White-Label Branding' },
  api_access:      { requiredPlan: 'elite',        label: 'Accesso API' },
};

const PLAN_LABELS = {
  professional: 'Professional',
  elite: 'Elite',
};

export function FeatureGate({ feature, children }) {
  const { features, tenant } = useTenant();

  // Se non c'è tenant o le features non sono caricate, mostra i figli
  if (!features || !tenant) return children;

  // Controlla se la feature è abilitata
  // Nota: undefined = feature non specificata = lascia passare (non bloccare)
  const enabled = features[feature] !== false;
  if (enabled) return children;

  // Feature bloccata — mostra overlay
  const info = FEATURE_PLAN_MAP[feature] || { requiredPlan: 'professional', label: feature };
  const planLabel = PLAN_LABELS[info.requiredPlan] || info.requiredPlan;
  const currentPlan = tenant.plan || 'essenziale';

  const handleContact = () => {
    window.open('mailto:info@francescorusso.work?subject=Upgrade%20piano%20Shangrilux', '_blank');
  };

  return (
    <div className="relative" data-testid={`feature-gate-${feature}`}>
      {/* Content underneath (blurred) */}
      <div className="pointer-events-none select-none" style={{ filter: 'blur(3px)', opacity: 0.3 }}>
        {children}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center z-10"
        style={{ background: 'rgba(10,10,10,0.75)', backdropFilter: 'blur(1px)' }}>
        <div className="text-center px-8 py-10 max-w-sm">
          {/* Lock icon */}
          <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center"
            style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)' }}>
            <Lock size={22} style={{ color: '#c9a84c' }} />
          </div>

          {/* Plan badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold tracking-widest uppercase mb-4"
            style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>
            Piano {planLabel}
          </div>

          <h3 className="font-serif text-xl font-light mb-2"
            style={{ color: 'var(--lux-text)' }}>
            {info.label}
          </h3>
          <p className="text-sm mb-6 leading-relaxed"
            style={{ color: 'var(--lux-text-muted)' }}>
            Questa funzione è disponibile dal piano <strong style={{ color: '#c9a84c' }}>{planLabel}</strong>.
            {currentPlan === 'essenziale' && info.requiredPlan === 'professional' && (
              <> Fai l&apos;upgrade per sbloccare smart home, portale fornitori, export e molto altro.</>
            )}
            {info.requiredPlan === 'elite' && (
              <> Disponibile esclusivamente nel piano Elite — white-label, i18n e accesso illimitato.</>
            )}
          </p>

          <button
            onClick={handleContact}
            data-testid={`upgrade-cta-${feature}`}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-sm transition-all hover:opacity-90"
            style={{ background: '#c9a84c', color: '#000' }}>
            Richiedi Upgrade
            <ArrowRight size={14} />
          </button>

          <p className="text-[10px] mt-3" style={{ color: 'var(--lux-text-muted-3)' }}>
            Piano attuale: <span className="uppercase tracking-widest">{currentPlan}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * useFeatureEnabled — hook per controllare se una feature è abilitata.
 * Restituisce { enabled, requiredPlan }
 */
export function useFeatureEnabled(feature) {
  const { features } = useTenant();
  if (!features) return { enabled: true };
  const enabled = features[feature] !== false;
  const info = FEATURE_PLAN_MAP[feature];
  return { enabled, requiredPlan: info?.requiredPlan };
}
